import { prefersReducedMotion } from './media';

const COLORS = [
  'var(--color-success)',
  'var(--color-accent)',
  'var(--color-warning)',
  'var(--color-danger)',
  '#a78bfa',
  '#f472b6',
  '#ffffff',
];
const PARTICLES = 70;
const MAX_MS = 2000;

export interface Piece {
  /** Where it ends up horizontally / vertically (px from the origin). */
  dx: number;
  dy: number;
  /** Highest point of the flight (negative = above the origin). */
  apexY: number;
  width: number;
  height: number;
  rotation: number;
  duration: number;
  delay: number;
  color: string;
}

/**
 * Plans one burst: every piece starts at the origin, flies up to its own height and falls while
 * drifting to its own spot across the WHOLE viewport width (not a row, not a fan: x, apex,
 * speed, size and spin are all random). Pure, so the spread can be tested without a browser.
 */
export function planConfetti(
  origin: { x: number; y: number },
  viewport: { width: number; height: number },
  random: () => number = Math.random,
  count = PARTICLES,
): Piece[] {
  return Array.from({ length: count }, (_, index) => {
    const size = 6 + random() * 6;
    return {
      dx: random() * viewport.width - origin.x,
      dy: viewport.height * (0.55 + random() * 0.5) - origin.y,
      apexY: -(origin.y * (0.15 + random() * 0.85)) - 20,
      width: size,
      height: size * (0.4 + random() * 0.6),
      rotation: (random() - 0.5) * 1440,
      duration: 900 + random() * 900,
      delay: random() * 120,
      color: COLORS[index % COLORS.length] ?? '#a78bfa',
    };
  });
}

/**
 * Confetti after a completion: starts at the pressed control and scatters over the whole
 * screen, ~1-2 s, never blocking clicks or scrolling. Plain DOM + Web Animations, no library.
 *
 * Returns true if a burst was started. Nothing happens - and false is returned - when the user
 * prefers reduced motion or the anchor is gone, so callers never need to check.
 */
export function burstConfetti(anchor: Element | null | undefined): boolean {
  if (!anchor?.isConnected || prefersReducedMotion()) return false;
  const rect = anchor.getBoundingClientRect();
  const origin = { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
  const viewport = { width: window.innerWidth, height: window.innerHeight };
  const layer = document.createElement('div');
  layer.setAttribute('aria-hidden', 'true');
  layer.dataset.testid = 'confetti';
  Object.assign(layer.style, {
    position: 'fixed',
    inset: '0',
    overflow: 'hidden',
    zIndex: '1000',
    pointerEvents: 'none',
  });
  for (const piece of planConfetti(origin, viewport)) {
    const element = document.createElement('span');
    Object.assign(element.style, {
      position: 'absolute',
      left: `${origin.x}px`,
      top: `${origin.y}px`,
      width: `${piece.width}px`,
      height: `${piece.height}px`,
      borderRadius: '1px',
      background: piece.color,
      opacity: '0',
    });
    layer.append(element);
    if (typeof element.animate === 'function') {
      const apexX = piece.dx * 0.55;
      element.animate(
        [
          { transform: 'translate(0, 0) rotate(0deg)', opacity: 1 },
          {
            transform: `translate(${apexX}px, ${piece.apexY}px) rotate(${piece.rotation * 0.4}deg)`,
            opacity: 1,
            offset: 0.35,
          },
          {
            transform: `translate(${piece.dx}px, ${piece.dy}px) rotate(${piece.rotation}deg)`,
            opacity: 0,
          },
        ],
        {
          duration: piece.duration,
          delay: piece.delay,
          easing: 'cubic-bezier(0.25, 0.6, 0.4, 1)',
          fill: 'backwards',
        },
      );
    }
  }
  // Inside an open <dialog> (top layer) so the burst is not painted under it.
  (anchor.closest('dialog') ?? document.body).append(layer);
  window.setTimeout(() => {
    layer.remove();
  }, MAX_MS);
  return true;
}
